import {
  getEncodableAudioCodecs,
  getEncodableVideoCodecs,
  type WebRendererAudioCodec,
  type WebRendererContainer,
  type WebRendererVideoCodec,
} from '@remotion/web-renderer';

export type OutputChoice = {
  container: WebRendererContainer;
  videoCodec: WebRendererVideoCodec;
  audioCodec: WebRendererAudioCodec | null;
};

const VIDEO_PREFERENCE: WebRendererVideoCodec[] = ['h264', 'vp9', 'vp8', 'av1'];

/**
 * Container/audio pairs ranked by *real-world playback*, not by what the spec
 * permits. mp4 legally carries Opus, but Apple does not decode Opus-in-mp4 —
 * not QuickTime, not Safari's audio element — so such a file plays with no
 * sound on an iPhone or a Mac and gives no hint why. webm+Opus is the pairing
 * Apple actually supports, and is what Safari itself records.
 *
 * Firefox ships no AAC encoder, so it lands on the second entry; picking the
 * first container that merely *had* some audio codec used to land it on the
 * third, and produced exactly that silent-on-Apple mp4.
 */
const PAIR_PREFERENCE: {
  container: WebRendererContainer;
  audioCodec: WebRendererAudioCodec;
}[] = [
  { container: 'mp4', audioCodec: 'aac' },
  { container: 'webm', audioCodec: 'opus' },
  { container: 'mp4', audioCodec: 'opus' },
];

const CONTAINERS: WebRendererContainer[] = ['mp4', 'webm'];

export const negotiateOutput = async (): Promise<OutputChoice> => {
  const probed = await Promise.all(
    CONTAINERS.map(async (container) => {
      const [video, audio] = await Promise.all([
        getEncodableVideoCodecs(container),
        getEncodableAudioCodecs(container),
      ]);
      return {
        container,
        videoCodec: VIDEO_PREFERENCE.find((c) => video.includes(c)) ?? video[0] ?? null,
        audio,
      };
    }),
  );

  const usable = probed.filter((p) => p.videoCodec !== null);
  if (usable.length === 0) {
    throw new Error(
      'This browser cannot encode video with WebCodecs. Chrome or Edge is the safe option.',
    );
  }

  // 1. A pairing that plays everywhere.
  for (const pair of PAIR_PREFERENCE) {
    const match = usable.find(
      (p) => p.container === pair.container && p.audio.includes(pair.audioCodec),
    );
    if (match) {
      return {
        container: match.container,
        videoCodec: match.videoCodec as WebRendererVideoCodec,
        audioCodec: pair.audioCodec,
      };
    }
  }

  // 2. An unusual audio codec still beats no audio at all.
  const withAnyAudio = usable.find((p) => p.audio.length > 0);
  if (withAnyAudio) {
    return {
      container: withAnyAudio.container,
      videoCodec: withAnyAudio.videoCodec as WebRendererVideoCodec,
      audioCodec: withAnyAudio.audio[0],
    };
  }

  // 3. Silent, because nothing here can encode sound.
  return {
    container: usable[0].container,
    videoCodec: usable[0].videoCodec as WebRendererVideoCodec,
    audioCodec: null,
  };
};
